import { useState } from "react";
import {
  Wrapper,
  Datagrid,
  DeleteActionIcon,
  Permission,
  ActionMenu,
} from "components";

import { Typography, Button } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { endpoints } from "appConfigs";
import Form from "./Form";
import View from "./View";
import Filters from "./Filters";
import { useAppState } from "components/AppProvider/AppProvider";
import { useFetch } from "hooks";
import { FormattedMessage } from "react-intl";
import { Visibility } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  titleBar: {
    padding: "1rem 0 !important",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  centered: {
    margin: "0 auto",
  },
  label: {
    color: "#ffffff",
  },
  status: {
    textTransform: "capitalize",
  },
}));

const Purchases = () => {
  const { authUser } = useAppState();
  const classes = useStyles();
  const [view, setView] = useState(false);
  const [edit, setEdit] = useState(null);
  const [filters, setFilters] = useState({});
  const { remove, loading } = useFetch(endpoints.dispensaryPurchases);
  return (
    <>
      <Wrapper padding={false}>
        <Grid container spacing={0} justify="center">
          <Grid item xs={10}>
            <Grid container spacing={1} direction="column" justify="center">
              <Grid item className={classes.titleBar}>
                <Typography variant="h5">
                  <FormattedMessage id="purchases" defaultMessage="Purchases" />
                </Typography>
                <Permission permission="add_dispensary_purchase">
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setEdit(true)}
                  >
                    <FormattedMessage
                      id="addPurchase"
                      defaultMessage="Add Purchase"
                    />
                  </Button>
                </Permission>
              </Grid>
              <Grid item>
                <Card>
                  <CardContent>
                    <Filters
                      filters={filters}
                      setFilters={setFilters}
                      authUser={authUser}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item>
                <Datagrid
                  url={endpoints.dispensaryPurchases}
                  filters={filters}
                  columns={[
                    ...(authUser.user_type === "admin"
                      ? [
                          {
                            headerName: (
                              <FormattedMessage
                                id="hospital"
                                defaultMessage="Hospital"
                              />
                            ),
                            field: "hospital.name",
                            renderCell: ({ row }) => row.hospital?.name,
                          },
                        ]
                      : []),
                    {
                      headerName: (
                        <FormattedMessage
                          id="dispensary"
                          defaultMessage="Dispensary"
                        />
                      ),
                      field: "dispensary.name",
                      renderCell: ({ row }) => row.dispensary?.name,
                    },
                    {
                      headerName: (
                        <FormattedMessage
                          id="supplier"
                          defaultMessage="Supplier"
                        />
                      ),
                      field: "dispensary_supplier.name",
                      renderCell: ({ row }) => row.dispensary_supplier?.name,
                    },
                    {
                      headerName: (
                        <FormattedMessage
                          id="products"
                          defaultMessage="Products"
                        />
                      ),
                      field: "products",
                      renderCell: ({ row }) => row.products?.length,
                    },
                    {
                      headerName: (
                        <FormattedMessage id="price" defaultMessage="Price" />
                      ),
                      field: "price",
                      renderCell: ({ row }) => {
                        const total = row.products.reduce(
                          (p, c) =>
                            p +
                            (c.qty * c.cost -
                              (c.qty * c.cost).percent(c.discount)),
                          0
                        );
                        return (
                          "₦" +
                          (
                            total +
                            total.percent(row.tax) +
                            row.shipping_charge
                          ).toLocaleString()
                        );
                      },
                    },
                    {
                      headerName: (
                        <FormattedMessage id="status" defaultMessage="Status" />
                      ),
                      field: "status",
                    },
                    {
                      headerName: (
                        <FormattedMessage id="action" defaultMessage="Action" />
                      ),
                      field: "action",
                      renderCell: ({ row }) => (
                        <>
                          <ActionMenu
                            menuItems={[
                              {
                                icon: <Visibility />,
                                label: "View",
                                onClick: () => {
                                  setView(row);
                                },
                              },
                              {
                                permission: "update_dispensary_purchases",
                                icon: <EditIcon />,
                                label: (
                                  <FormattedMessage
                                    id="editPurchase"
                                    defaultMessage="Edit Purchase"
                                  />
                                ),
                                onClick: () => setEdit(row),
                              },
                              {
                                permission: "remove_dispensary_purchases",
                                icon: <DeleteIcon />,
                                label: "Delete",
                                confirmationTitle: (
                                  <FormattedMessage
                                    id="deletePurchase"
                                    defaultMessage="Delete Purchase"
                                  />
                                ),
                                confirmation: (
                                  <FormattedMessage
                                    id="deleteConfirmation_dispensary_purchase"
                                    defaultMessage="Are you sure want to remove this purchase?"
                                  />
                                ),
                                onClick: () =>
                                  remove({ ids: row.id })
                                    .then(({ data }) => {
                                      if (data.success) {
                                        setFilters((prev) => ({ ...prev }));
                                      }
                                    })
                                    .catch((err) => {
                                      NotificationManager.error(err.message);
                                    }),
                              },
                            ]}
                          />
                          <Permission>
                            <DeleteActionIcon
                              disabled={loading}
                              message={
                                <FormattedMessage
                                  id="deleteConfirmation_dispensary_purchase"
                                  defaultMessage="Are you sure want to remove this purchase?"
                                />
                              }
                              deleteRecord={() => {
                                remove({ ids: row.id })
                                  .then(({ data }) => {
                                    if (data.success) {
                                      setFilters((prev) => ({ ...prev }));
                                    }
                                  })
                                  .catch((err) => {
                                    NotificationManager.error(err.message);
                                  });
                              }}
                            />
                          </Permission>
                        </>
                      ),
                    },
                  ]}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Form
          authUser={authUser}
          open={!!edit}
          edit={edit}
          setEdit={setEdit}
          onSuccess={(newSubPackage) => {
            setFilters((prev) => ({ ...prev }));
            setEdit(null);
          }}
        />
      </Wrapper>
      <View authUser={authUser} view={view} setView={setView} />
    </>
  );
};

export default Purchases;
